'use client';

import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldError, get, useFormContext } from 'react-hook-form';

import { CommonFormContext } from '@/components/CommonForm';
import { CommonCheckbox } from '@/components/CommonForm/CommonCheckbox';
import { isPhoneNumberComplete } from '@/components/CommonForm/CommonPhoneInput';

import { checkBoxSMS, checkBoxSubscribe } from './constants';

import { PropsWithClasses } from '@lamesarv-sdk/types';
import { IFormProps } from './types';

export interface IBaseButtonProps extends IFormProps, PropsWithClasses {
  text: string;
  nameSlugForm: string;
  nameSlugButton: string;
  buttonType?: 'button' | 'submit' | 'reset';
  styleClasses?: string;
  extraClasses?: string;
  onClick?: () => void;
  forceDisabled?: boolean;
}

export const BaseButton = (props: Partial<IBaseButtonProps>) => {
  const { isDisabled } = useContext(CommonFormContext);

  return (
    <button
      type={props.buttonType || 'button'}
      onClick={props.onClick}
      disabled={props.forceDisabled !== undefined ? props.forceDisabled : isDisabled}
      className={twMerge(
        props.styleClasses,
        props.classes_next,
        props.extraClasses,
        isDisabled && 'disabled:opacity-50 disabled:cursor-not-allowed',
      )}
      data-tag-group={props.data_tag ? `${props.data_tag}--${props.nameSlugForm}` : props.nameSlugForm}
      data-tag={
        props.data_tag
          ? `${props.data_tag}--${props.nameSlugForm}-${props.nameSlugButton}`
          : `${props.nameSlugForm}-${props.nameSlugButton}`
      }
    >
      {props.text}
    </button>
  );
};

export interface IBaseSubmitProps extends IFormProps, PropsWithClasses {
  formNameSlug: string;
  styleClasses?: string;
  extraClasses?: string;
}

export const BaseSubmit = (props: Partial<IBaseSubmitProps>) => {
  return (
    <BaseButton
      {...props}
      buttonType="submit"
      nameSlugForm={props.formNameSlug}
      nameSlugButton="submit"
      text={props.submit_button_text || 'Submit'}
    />
  );
};

export const BaseCaptchaError = () => {
  return (
    <div className="w-full flex flex-col gap-4 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center justify-center">
        <span>Form validation failed.</span>
        <span className="flex flex-row gap-2 items-center">
          Please Call{' '}
          <a href="tel:877-641-8052" className="flex gap-2 items-center decoration-dashed underline underline-offset-4">
            <FontAwesomeIcon icon={faPhone} className="size-3.5" />
            <span>877-641-8052</span>
          </a>
        </span>
        <span>for your inquiry</span>
      </div>
    </div>
  );
};

interface BaseSubscribeCheckboxProps {
  name?: string;
  htmlContent: string;
}

export const BaseSubscribeCheckbox = ({ htmlContent, name }: BaseSubscribeCheckboxProps) => {
  return (
    <CommonCheckbox name={name || 'subscribe'} className="items-start" classInput="size-4">
      <div
        dangerouslySetInnerHTML={{
          __html: htmlContent || checkBoxSubscribe,
        }}
      />
    </CommonCheckbox>
  );
};

interface BaseOptInCheckboxProps {
  nameDependency: string;
  name?: string;
  htmlContent: string;
}

export const BaseOptInCheckbox = ({ htmlContent, name, nameDependency }: BaseOptInCheckboxProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const errorDependency = get(errors, nameDependency) as FieldError | null;
  const fieldValue = isPhoneNumberComplete(watch(nameDependency));

  const isValid = fieldValue && !errorDependency;

  if (!isValid) {
    return null;
  }

  return (
    <CommonCheckbox name={name || 'smsOptIn'} className="items-start" classInput="size-4">
      <div
        dangerouslySetInnerHTML={{
          __html: htmlContent || checkBoxSMS,
        }}
      />
    </CommonCheckbox>
  );
};
